import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email,
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_ai-persona-connect/artifacts/d3pkba7d_copy_8D43DC47-40F7-432E-B350-F206250169DA.png" alt="Animus" className="w-12 h-12 object-contain" />
            <span className="font-bold text-2xl tracking-tight text-white font-['Unbounded']">Animus</span>
          </Link>
        </div>

        <div className="bg-[#0F1115] border border-white/10 rounded-sm p-8">
          {sent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2" data-testid="reset-sent-title">Check your email</h2>
              <p className="text-[#A1A1AA] text-sm mb-6">
                If an account exists for <strong className="text-white">{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <Link to="/login">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#71717A] hover:text-white mb-4 transition-colors" data-testid="back-link">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
              <h1 className="text-2xl font-bold text-white mb-2 font-['Unbounded']">Forgot password?</h1>
              <p className="text-[#A1A1AA] text-sm mb-6">Enter your email and we'll send you a reset link.</p>

              {error && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-sm text-[#E11D48] text-sm" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#A1A1AA]">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="pl-10 bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-sm"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-sm py-3"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : 'Send Reset Link'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
